<template>
	<v-app>
		<main v-if="formReady" class="main">
			<v-row v-if="ready" no-gutters class="justify-center mb-0" :class="display.smAndDown.value ? 'mobile-container-row' : ''">
				<v-col cols="11" md="3" lg="4" class="on-top">
					<img
						v-if="!organizationLogo"
						id="liveswitch-logo"
						:class="posterClass"
						src="@/assets/ls-contact-3d-color-logo-white-font.svg" />
					<img v-else :class="posterClass" :src="organizationLogo" />
				</v-col>
				<v-col cols="11" md="7" lg="4" :class="display.smAndDown.value ? 'mt-4' : 'mt-12'">
					<v-card rounded="xl" style="overflow: hidden">
						<div class="card-inner">
							<v-row no-gutters class="header pa-4">
								<v-col cols="6" class="text-h5 my-auto pl-3"
									><span id="header-text" class="font-weight-600">{{ model.headline }}</span>
								</v-col>
								<v-col cols="6">
									<img
										:src="LsContactDashboardRecordingRequestImageDesktopWebp"
										class="recording-video-img"
										alt="A person recording a video with his cell phone." />
								</v-col>
							</v-row>

							<div v-if="shareUrl" class="recording-information pa-4">
								<div class="d-flex flex-row align-center justify-center mb-4">
									<v-icon color="success" size="32" class="mr-3">mdi-check-circle</v-icon>
									<div class="text-h6 font-weight-600">Your form has been submitted successfully.</div>
								</div>

								<div v-if="display.smAndDown.value" class="success-mobile text-center">
									<div class="mb-6">Click below to start recording now.</div>
									<v-btn
										id="start-recording-btn"
										color="primary"
										class="text-none"
										height="54"
										block
										:href="shareUrl"
										target="_blank">
										Start Recording
									</v-btn>
								</div>

								<div v-else class="success-desktop text-center">
									<div class="mb-4">Scan this QR code with your phone to start recording.</div>
									<div class="qr-code-container d-flex justify-center pa-4 bg-white rounded-lg mb-4">
										<QRcodeVue :value="shareUrl" :size="200" level="H" />
									</div>
								</div>

								<div v-if="organizationFormSMSEnabled" class="mb-4 mt-8 text-center text-body-2 opacity-70">
									We have also sent you a text message with the recording link.
								</div>
							</div>

							<v-form v-if="!shareUrl" ref="form" lazy-validation class="pa-4" autocomplete="on">
								<v-container v-if="formReady" class="pb-0">
									<div v-for="(field, index) in model.FormFields" :key="field.name">
										<div style="position: relative" class="text-field-wrapper">
											<label :for="getPublicFormInputAttributes(field).id" class="text-h7 mb-2"
												><FieldLabel :label="getLabel(field)"
											/></label>
											<EmailTextField
												v-if="isEmailField(field)"
												v-model="formData[field.id]"
												variant="outlined"
												hide-details="auto"
												:autofocus="index == 0"
												:id="getPublicFormInputAttributes(field).id"
												:name="getPublicFormInputAttributes(field).name"
												:autocomplete="getPublicFormInputAttributes(field).autocomplete"
												:rules="getValidateRules(field)"
												:max-length="getMaxLength(field)"
												class="field-border-radius"
												@input="inputChanged"></EmailTextField>

											<div v-else-if="isPhoneNumberField(field)" @paste.prevent="pasteEvent">
												<!-- .trim (LSP-17337): a v-model modifier normally does nothing on a
												     component - Vue just forwards `modelModifiers` and the child has to
												     honor it - but vue-tel-input v9.8.0 declares it (via defineModel()),
												     so Vue's own emit path applies the trim before phoneNumber is
												     assigned. That placement is the point: trimming inside the emit beats
												     writing the model back from @input, which the widget's own re-emit
												     overwrites. Internal spaces ("604 555") are untouched. Note this is
												     plain String.trim(), so it does NOT strip zero-width characters
												     (U+200B et al); valid-characters-only below filters those at the
												     input instead. -->
												<vue-tel-input
													v-bind="vueTelProps"
													v-model.trim="phoneNumber"
													:input-options="{ ...vueTelProps.inputOptions, ...getPublicFormInputAttributes(field) }"
													valid-color="#000000"
													:autofocus="index == 0"
													:border-radius="0"
													:valid-characters-only="true"
													:required="isRequired(field)"
													:style="{
														height: '54px',
														marginBottom: !validPhoneNumber && showPhoneNumberFieldErrors ? '8px' : '12px',
														border:
															!validPhoneNumber && showPhoneNumberFieldErrors
																? '2px solid red !important'
																: ''
													}"
													:rules="getValidateRules(field)"
													@validate="validatePhoneNumber"
													@input="matchPhone"
													@blur="phoneNumberBlur"
													@focus="phoneNumberFocus" />
												<div
													v-if="!validPhoneNumber && showPhoneNumberFieldErrors"
													class="v-text-field__details"
													style="margin-bottom: 8px; padding: 0 12px">
													<div class="v-messages theme--light error--text" role="alert">
														<div class="v-messages__wrapper">
															<div class="v-messages__message">
																{{ getValidateRules(field) }}
															</div>
														</div>
													</div>
												</div>
											</div>

											<v-text-field
												v-else-if="field.type === FieldType.Text || field.type === FieldType.Name"
												v-model="formData[field.id]"
												variant="outlined"
												hide-details="auto"
												:autofocus="index === 0"
												:id="getPublicFormInputAttributes(field).id"
												:name="getPublicFormInputAttributes(field).name"
												:autocomplete="getPublicFormInputAttributes(field).autocomplete"
												:rules="getValidateRules(field)"
												:counter="getMaxLength(field)"
												:counter-value="countCodePoints"
												class="field-border-radius"
												@input="inputChanged"></v-text-field>

											<v-text-field
												v-else-if="field.type === FieldType.Date"
												v-model="formData[field.id]"
												type="date"
												variant="outlined"
												hide-details="auto"
												:autofocus="index === 0"
												:id="getPublicFormInputAttributes(field).id"
												:name="getPublicFormInputAttributes(field).name"
												:autocomplete="getPublicFormInputAttributes(field).autocomplete"
												:rules="getValidateRules(field)"
												class="field-border-radius"
												@input="inputChanged"></v-text-field>

											<v-text-field
												v-else-if="field.type === FieldType.Number"
												v-model="formData[field.id]"
												type="text"
												inputmode="decimal"
												variant="outlined"
												hide-details="auto"
												:autofocus="index === 0"
												:id="getPublicFormInputAttributes(field).id"
												:name="getPublicFormInputAttributes(field).name"
												:autocomplete="getPublicFormInputAttributes(field).autocomplete"
												:rules="getValidateRules(field)"
												:counter="getMaxLength(field)"
												:counter-value="countCodePoints"
												class="field-border-radius"
												@input="inputChanged"
												@keydown="filterNumberKeydown"
												@paste.prevent="pasteNumberEvent($event, field.id)"></v-text-field>

											<v-textarea
												v-else-if="field.type === FieldType.TextArea"
												v-model="formData[field.id]"
												variant="outlined"
												hide-details="auto"
												:autofocus="index === 0"
												:id="getPublicFormInputAttributes(field).id"
												:name="getPublicFormInputAttributes(field).name"
												:autocomplete="getPublicFormInputAttributes(field).autocomplete"
												:rows="2"
												:rules="getValidateRules(field)"
												:counter="getMaxLength(field)"
												:counter-value="countCodePoints"
												@input="inputChanged" />
										</div>
									</div>

									<!-- Honeypot field (should remain empty). Its wire name and autofill-suppression
									     attributes come from publicFormHoneypotAttributes — this widget used to hardcode
									     the legacy `__hp_email` name with `autocomplete="off"` (which Chrome ignores for
									     email/address autofill), the exact false-positive V2 already fixed in LSP-19625.
									     The backend still accepts both names (HONEYPOT_FIELD_IDS), so a cached PRE-DEPLOY
									     bundle submitting the legacy name and leaving it EMPTY keeps working exactly as
									     before. It does NOT protect a cached bundle whose honeypot got autofilled by
									     Chrome (the LSP-19625 failure mode this fix exists to close for new page loads) —
									     that visitor is rejected until the tab reloads and picks up this bundle. Accepted,
									     time-bounded transition risk (Cursor Bugbot / PR #5473 round 7 review); see the
									     plan doc for the full reasoning. -->
									<input
										v-model="honeypotValue"
										v-bind="publicFormHoneypotAttributes"
										type="text"
										tabindex="-1"
										style="position: absolute; left: -9999px; opacity: 0; pointer-events: none"
										aria-hidden="true" />

									<!-- Form ID field (set with JavaScript) -->
									<input type="hidden" name="__fid" :value="formIdValue" />

									<!-- LSP-16526: the security check's state has to be visible. Submitting while it was still
									     loading used to throw "No reCAPTCHA clients exist" and dead-end on a 5s generic snackbar,
									     so the lead was never sent. -->
									<v-alert
										v-if="captchaState === 'unavailable'"
										type="warning"
										variant="tonal"
										density="compact"
										class="mb-2"
										data-test-id="webform-captcha-unavailable">
										We couldn't load the security check needed to submit this form. Check your connection and try again
										— what you've typed is kept.
										<template #append>
											<v-btn variant="text" data-test-id="webform-captcha-retry" @click="retryCaptcha"
												>Try Again</v-btn
											>
										</template>
									</v-alert>
									<div
										v-else-if="captchaSubmitQueued"
										class="mb-2 text-medium-emphasis"
										role="status"
										data-test-id="webform-captcha-waiting">
										Checking that you're not a robot — your form will be submitted automatically.
									</div>

									<v-row class="button justify-end mt-2" no-gutters>
										<div
											ref="recaptchaElement"
											class="g-recaptcha"
											data-callback="onSubmit"
											data-size="invisible"></div>
										<v-col :cols="display.smAndUp.value ? 'auto' : '12'">
											<v-btn
												id="send-recording-invite-btn"
												:loading="saving || captchaSubmitQueued"
												:disabled="formDeleted"
												color="primary"
												class="my-4 text-right px-4 d-flex flex-column align-center justify-center"
												height="auto"
												min-height="64px"
												style="width: 100%; font-size: 20px"
												@click="validateForm">
												<div class="d-flex flex-row align-center justify-center text-center text-wrap">
													<span style="max-width: 100%; text-transform: none">Create Photos and Videos</span>
													<v-icon size="22" style="color: white" class="px-2"
														>icon-circle-arrow-right-stroke</v-icon
													>
												</div>
											</v-btn>
										</v-col>
									</v-row>
								</v-container>
							</v-form>
						</div>
					</v-card>
				</v-col>
				<v-col cols="12" md="2" lg="4"></v-col>
			</v-row>
			<v-snackbar id="snackbar" v-model="showSnackbar" top :timeout="snackbarTimeout">
				{{ errorMessage }}
			</v-snackbar>
		</main>
		<PageNotFound v-else-if="formError" />
	</v-app>
</template>

<script lang="ts" setup>
	import FormField from "./types/forms/FormField";
	import Form from "./types/forms/Form";
	import { onMounted, onBeforeUnmount, ref, computed, nextTick } from "vue";
	import { FieldType } from "./types/forms/FieldType";
	import { FieldValidationType } from "./types/forms/FieldValidationType";
	import {
		getTrimmedPhoneFromClipboard,
		getNumberFromClipboard,
		copyToClipboard,
		calculateTimeout,
		getDiagnosticError,
		getPublicSubmissionFailureDiagnostics
	} from "@/helpers/utilities";
	import QRcodeVue from "qrcode.vue";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { countCodePoints } from "@common-ui/utilities/characterCount";

	import api from "@/api";
	import { logError, logInfo, logWarn } from "@/helpers/logError";
	import {
		formatMaxLengthErrorMessage,
		inheritsDefaultMaxLength,
		isNameCappedField,
		resolvesToEmailField,
		resolvesToPhoneField,
		resolvePublicFormMaxLength,
		VuetifyValidation,
		FIELD_LENGTHS,
		FIELD_NUMBER_BOUNDS
	} from "./helpers/Validation";
	import { FormSubmissionField } from "./types/forms/FormSubmissionField";
	import { FormSubmission } from "./types/forms/FormSubmission";
	import constants from "./api/constants";
	import timezones from "./helpers/timezones";
	import { getDateTokens, OrgDateFormat } from "./helpers/dateFormat";
	import { dedupeCountryCodes, getVueTelInputOptions, getVueTelProps } from "@/helpers/phone";
	import {
		getPublicFormInputAttributes as getAutofillInputAttributes,
		PUBLIC_FORM_HONEYPOT_FIELD_NAME,
		publicFormHoneypotAttributes
	} from "@/helpers/publicFormAutofill";
	import PageNotFound from "@/components/PageNotFound.vue";
	import { useDisplay } from "vuetify";
	import LsContactDashboardRecordingRequestImageDesktopWebp from "@/assets/ls-contact-dashboard-recording-request-image-desktop.webp";
	import { useToast } from "vue-toastification";
	import { filterNumberKeydown } from "./helpers/utilities";

	const ready = ref<boolean>(false);
	const formReady = ref<boolean>(false);
	const formError = ref<boolean>(false);
	const showSnackbar = ref<boolean>(false);
	const snackbarTimeout = ref<number>(5000);
	const model = ref<Form>({} as Form);
	const getPublicFormInputAttributes = (field: Partial<FormField>) => getAutofillInputAttributes(field, model.value.FormFields ?? []);
	const organizationLogo = ref<string>();
	const shareUrl = ref<string>("");
	const toast = useToast();
	const form = ref();
	const display = useDisplay();
	const validPhoneNumber = ref<boolean>(false);
	const validationRules: Map<string, Array<Function>> = new Map<string, Array<Function>>();
	const formData = ref({});
	const saving = ref<boolean>(false);
	const phoneNumber = ref<string>();
	const showPhoneNumberFieldErrors = ref<boolean>(false);
	const errorMessage = ref<string>("Unfortunately we could not process your request at this time. Please try again.");
	const recaptchaSiteKey = ref<string>(window.env.VITE_RECAPTCHA_SITE_KEY);
	const recaptchaElement = ref();
	// ~5s of 500ms polls before the widget is declared unreachable (LSP-16526).
	const CAPTCHA_MAX_LOAD_ATTEMPTS = 10;
	// The first poll always runs before the form (and so the recaptcha element) exists, so only a
	// still-waiting poll ~1s in is worth a log line — otherwise every healthy page view logs one.
	const CAPTCHA_WAIT_LOG_ATTEMPT = 2;
	const captchaLoadCount = ref(0);
	let captchaRetryTimeout: ReturnType<typeof setTimeout> | undefined;
	let isUnmounted = false;
	const captchaEnabled = ref(true);
	// LSP-16526: the widget's lifecycle has to be observable, otherwise a submit cannot tell
	// "not rendered yet" (wait for it) from "never going to render" (say so, offer a retry)
	// and ends up calling grecaptcha.execute() on a client that does not exist.
	const captchaState = ref<"loading" | "ready" | "unavailable">("loading");
	// A submit that arrived before the widget was ready, replayed once it renders.
	const captchaSubmitQueued = ref(false);
	let captchaRendered = false;
	let captchaCallbackExpected = false;
	// Google's widget handle, needed to reset() a widget whose token expired or whose challenge
	// failed — without it the only "recovery" is executing the same broken state again.
	let captchaWidgetId: number | undefined;
	// One outstanding api.js request at a time. Two concurrent loads re-initialize
	// window.grecaptcha and orphan an already-rendered widget, which is how this ticket's
	// "No reCAPTCHA clients exist" comes back one retry later — but a request that has FAILED is
	// not outstanding, and refusing to ask again after a failure would make the retry decorative.
	// Only a failure clears this; success is signalled by window.captchaLoaded instead, which is
	// set strictly later than the script element's load event.
	let captchaScriptRequestPending = false;
	const organizationFormSMSEnabled = ref(false);
	const formDeleted = ref<boolean>(false);
	const honeypotValue = ref("");
	const formIdValue = ref("");
	const defaultCountryCode = ref<string | null>(null);
	const orgDateFormat = ref<OrgDateFormat>("MDY");
	const vueTelProps = ref(
		getVueTelProps({
			preferredCountries: ["US", "AU", "CA", "GB"],
			mode: "international",
			required: true,
			dropdownOptions: {
				showFlags: true,
				showSearchBox: true
			},
			inputOptions: getVueTelInputOptions({
				required: true,
				placeholder: "Phone Number (Required)",
				autofocus: false,
				autocomplete: "on"
			}),
			disabledFormatting: false,
			wrapperClasses: "country-phone-input",
			validCharactersOnly: true
		})
	);

	onMounted(async () => {
		ready.value = true;

		const captchaParameter = window.env.VITE_CAPTCHA_BYPASS;

		try {
			if (new URLSearchParams(window.location.search).get(captchaParameter) === "false") {
				captchaEnabled.value = false;
			}
		} catch (err) {
			console.error(err);
		}

		try {
			const hash = window.location.hash;
			if (hash != "") {
				const formId = hash.substring(1);
				const response = await api.getFormInsecure(formId);
				setupFormData(response.form);
				model.value = response.form;
				organizationLogo.value = response.organizationLogoUrl;
				organizationFormSMSEnabled.value = response.formSMSEnabled;
				defaultCountryCode.value = response.defaultCountryCode || null;
				orgDateFormat.value = (response.dateFormat as OrgDateFormat) || "MDY";
				if (defaultCountryCode.value) {
					const country = defaultCountryCode.value.toUpperCase();
					const basePreferred = ["US", "AU", "CA", "GB"];
					const filtered = basePreferred.filter((code) => code !== country);
					vueTelProps.value = {
						...vueTelProps.value,
						preferredCountries: dedupeCountryCodes([country, ...filtered])
					};
				}
				setValidationRules();
				formReady.value = true;
				// Set form ID value for spam protection (requires JavaScript)
				if (model.value.id) {
					formIdValue.value = model.value.id;
				}

				// break-it round 2: start polling only once the form — and therefore the recaptcha
				// element — is in the DOM. Started from onMounted instead, every poll before this
				// GET resolved failed on the element ref and spent the give-up budget, so a slow
				// FORM fetch made a perfectly healthy captcha report itself as unavailable. Google's
				// api.js is requested by index.html at page load, so nothing is delayed by waiting.
				await nextTick();
				loadCaptcha();
			}
		} catch (error) {
			formError.value = true;
			if (error?.code === 404) {
				formDeleted.value = true;
			}
			// if form doesnt exist, we don't want to log it
		}
	});

	onBeforeUnmount(() => {
		isUnmounted = true;
		if (captchaRetryTimeout !== undefined) {
			clearTimeout(captchaRetryTimeout);
			captchaRetryTimeout = undefined;
		}
	});

	/** index.html's api.js onload sets this untyped global; read it in one typed place. */
	function isCaptchaScriptLoaded(): boolean {
		return !!(window as unknown as { captchaLoaded?: boolean }).captchaLoaded;
	}

	function loadCaptcha() {
		// If reCAPTCHA takes longer than 5 seconds to load, something went wrong. The user is
		// then told so (and offered a retry) instead of the failure staying in the console.
		if (isUnmounted) return;

		// break-it round 1: with the captcha bypassed (VITE_CAPTCHA_BYPASS query parameter, which
		// every public-form E2E uses) submit never goes near grecaptcha, so polling for a widget
		// nobody will execute can only produce a bogus "couldn't load the security check" notice.
		if (!captchaEnabled.value) return;

		const captchaScriptLoaded = isCaptchaScriptLoaded();
		const recaptchaElementReady = !!recaptchaElement.value;

		if (captchaLoadCount.value > CAPTCHA_MAX_LOAD_ATTEMPTS) {
			// LSP-16526 silent-failure diagnostics: record WHICH condition never came true, so the
			// next incident does not need reconstructing from "grecaptcha threw" alone.
			logWarn("[webform-captcha] gave up loading the widget; submit will show the recoverable notice", {
				attempts: captchaLoadCount.value,
				captchaScriptLoaded,
				recaptchaElementReady,
				formId: formIdValue.value
			});
			setCaptchaUnavailable();
			return;
		}

		if (!captchaScriptLoaded || !recaptchaElementReady) {
			if (captchaLoadCount.value === CAPTCHA_WAIT_LOG_ATTEMPT) {
				logWarn("[webform-captcha] widget not ready, retrying", {
					attempt: captchaLoadCount.value + 1,
					captchaScriptLoaded,
					recaptchaElementReady
				});
			}
			captchaRetryTimeout = setTimeout(() => {
				captchaRetryTimeout = undefined;
				loadCaptcha();
			}, 500);
			captchaLoadCount.value++;
			return;
		}

		try {
			captchaWidgetId = grecaptcha.render(recaptchaElement.value, {
				sitekey: recaptchaSiteKey.value,
				// Without these two, a challenge that errors or a token that expires leaves the
				// user with no feedback and no way forward — the same dead end as the original bug.
				"error-callback": () => {
					logWarn("[webform-captcha] the challenge reported an error", { formId: formIdValue.value });
					setCaptchaUnavailable();
				},
				"expired-callback": () => {
					logWarn("[webform-captcha] the challenge token expired; the next submit will re-challenge", {
						formId: formIdValue.value
					});
					resetCaptchaWidget();
				},
				callback: async (response) => {
					if (!captchaCallbackExpected) return;
					const captchaApi = grecaptcha as unknown as { getResponse?: (widgetId?: number) => string };
					if (captchaApi.getResponse && captchaApi.getResponse(captchaWidgetId) !== response) return;
					captchaCallbackExpected = false;
					console.log("reCAPTCHA Validation: Status=Success");

					// break-it round 2: re-check before posting — the user can edit (or clear) fields
					// while the challenge is open, and the retry gesture reaches this callback too.
					if (!(await validateForSubmit())) {
						logWarn("[webform-captcha] captcha passed but the form is no longer valid; not submitting", {
							formId: formIdValue.value
						});
						resetCaptchaWidget();
						return;
					}

					await submitForm();
				}
			});
		} catch (err) {
			// A blocked/partial recaptcha bundle, a bad site key, or a re-render of the same
			// element all throw here. Without this the component reported "ready" and the throw
			// resurfaced later as "No reCAPTCHA clients exist" on submit.
			logError("[webform-captcha] grecaptcha.render() failed", err, {
				file: "WebForm",
				method: "loadCaptcha",
				formId: formIdValue.value
			});
			setCaptchaUnavailable();
			return;
		}

		captchaRendered = true;
		captchaState.value = "ready";

		if (captchaSubmitQueued.value) {
			captchaSubmitQueued.value = false;
			logInfo("[webform-captcha] widget ready, replaying the queued submit");
			// break-it round 1: replay the whole gate, not just the captcha. The fields stay
			// editable during the wait, so what was valid when the user tapped Submit may not be
			// valid now — going straight to executeCaptcha() could post a lead with a blank Name.
			void validateForm();
		}
	}

	function setCaptchaUnavailable() {
		captchaState.value = "unavailable";
		captchaSubmitQueued.value = false;
		captchaCallbackExpected = false;
	}

	function executeCaptcha() {
		try {
			captchaCallbackExpected = true;
			grecaptcha.execute();
		} catch (err) {
			// grecaptcha.execute() throws "No reCAPTCHA clients exist" when no widget is rendered.
			logError("[webform-captcha] grecaptcha.execute() failed", err, {
				file: "WebForm",
				method: "verifyCaptcha",
				formId: formIdValue.value,
				captchaState: captchaState.value,
				captchaRendered
			});
			setCaptchaUnavailable();
		}
	}

	function resetCaptchaWidget() {
		captchaCallbackExpected = false;
		if (!captchaRendered) return;

		try {
			grecaptcha.reset(captchaWidgetId);
		} catch (err) {
			logError("[webform-captcha] grecaptcha.reset() failed", err, { file: "WebForm", formId: formIdValue.value });
			setCaptchaUnavailable();
		}
	}

	function retryCaptcha() {
		// break-it round 1: one retry gesture, one loader chain. Two clicks landing in the same
		// task would inject two scripts and render the same element twice, and Google's second
		// render() throws "already been rendered" — which the catch above would report as
		// "unavailable" on a widget that had in fact rendered fine.
		if (captchaState.value !== "unavailable") return;

		if (captchaRetryTimeout !== undefined) {
			clearTimeout(captchaRetryTimeout);
			captchaRetryTimeout = undefined;
		}

		// The widget rendered but execute() still failed — re-rendering the same element would
		// throw "already been rendered", so reset the existing widget (which clears an expired or
		// failed challenge) and try it again. Executing the same unreset state just repeats the
		// failure, which is the loop this ticket is about.
		if (captchaRendered) {
			captchaState.value = "ready";
			resetCaptchaWidget();
			if (captchaState.value === "ready") {
				executeCaptcha();
			}
			return;
		}

		// index.html requests api.js once, async. If that request never landed (blocked domain,
		// dropped connection) there is nothing for loadCaptcha() to poll, so retrying has to
		// re-request the script itself — otherwise the retry is decorative. Retrying in place,
		// rather than reloading the page, keeps what the user already typed.
		// Ask for the script again only when nothing we asked for is still outstanding: a second
		// concurrent load could orphan a widget rendered from the first, but a load that already
		// failed leaves nothing to orphan and must not block a further attempt.
		if (!isCaptchaScriptLoaded() && !captchaScriptRequestPending) {
			captchaScriptRequestPending = true;
			const script = document.createElement("script");
			script.src = "https://www.google.com/recaptcha/api.js?onload=captchaLoadCallback&render=explicit";
			script.async = true;
			script.defer = true;
			// Silent-failure rule: a retry whose request never lands must be distinguishable in
			// Datadog from "Google was slow again".
			script.onerror = () => {
				// Settled (badly): a further retry is allowed to ask again.
				captchaScriptRequestPending = false;
				logWarn("[webform-captcha] the retry request for api.js failed", { formId: formIdValue.value });
			};
			// Deliberately does NOT clear the pending flag: this event fires when api.js itself is
			// fetched, but window.captchaLoaded is only set later, by Google's own onload callback
			// once recaptcha has finished initializing. Clearing here would let a give-up landing
			// in that gap start a second concurrent load, re-initialize grecaptcha and orphan the
			// widget. The flag needs no success path — once isCaptchaScriptLoaded() is true the
			// injection above is skipped anyway.
			script.onload = () => {
				logInfo("[webform-captcha] the retry request for api.js loaded");
			};
			document.head.appendChild(script);
		}

		captchaLoadCount.value = 0;
		captchaState.value = "loading";
		loadCaptcha();
	}

	function setupFormData(form: Form) {
		for (let field of form.FormFields) {
			formData.value[field.id] = "";
		}
	}

	function setValidationRules() {
		for (let field of model.value.FormFields) {
			const rules: Function[] = validationRules.get(field.id) ?? [];
			let hasValidMaxLength = false;
			let maxLengthErrorTemplate: unknown;
			let hasMaxLengthValidator = false;
			let hasMaxValue = false;
			let hasMinValue = false;
			let numberMaxLengthRule: { maxLength: number; errorMessage: string } | undefined;
			let numberLengthRuleInsertionIndex = rules.length;

			if (field.type === FieldType.Number) {
				rules.push(VuetifyValidation.BasicNumberValidation("Please enter a valid number").isValid);
				const customDecimal = field.FieldValidators.find(
					(v: any) => v.type === FieldValidationType.Custom && v.customValidator === "DecimalNumber"
				);
				if (customDecimal) {
					rules.push(VuetifyValidation.DecimalNumberValidation(customDecimal.errorMessageTpl).isValid);
				} else {
					rules.push(VuetifyValidation.DecimalNumberValidation("Up to 2 decimal places").isValid);
				}
				numberLengthRuleInsertionIndex = rules.length;
			}

			for (let validator of field.FieldValidators) {
				switch (validator.type) {
					case FieldValidationType.Custom:
						if (validator.customValidator === "FirstAndLastName") {
							rules.push(VuetifyValidation.NameRule(validator.errorMessageTpl).isValid);
						} else if (validator.customValidator === "EmailOrPhone") {
							// TODO: MK - don't think we need this right now but leaving for reference in the future if we decide we need it
							//emailOrPhoneFields.add(field.id);
							//rules.push(CheckEmailAndPhone(validator.errorMessageTpl));
						} else if (validator.customValidator === "DecimalNumber") {
							if (field.type !== FieldType.Number) {
								rules.push(VuetifyValidation.DecimalNumberValidation(validator.errorMessageTpl).isValid);
							}
						}
						break;
					case FieldValidationType.Email:
						rules.push(
							VuetifyValidation.EmailValidation(
								validator.errorMessageTpl,
								`Email address must not exceed ${FIELD_LENGTHS.EMAIL} characters.`
							).isValid
						);
						field.type = FieldType.Email;
						break;
					case FieldValidationType.Phone:
						rules.push(VuetifyValidation.PhoneNumberValidation(validator.errorMessageTpl).isValid);
						field.type = FieldType.Phone;
						break;
					case FieldValidationType.MaxLength: {
						if (hasMaxLengthValidator) {
							logWarn("WebForm: dropping duplicate MaxLength validator", {
								fieldId: field.id,
								value: validator.value
							});
							break;
						}
						hasMaxLengthValidator = true;
						maxLengthErrorTemplate = validator.errorMessageTpl;
						// Number fields retain a 15-character safety ceiling while honoring a stricter configured MaxLength.
						const maxLength = resolvePublicFormMaxLength(field, validator);
						if (validator.value != null && maxLength === undefined) {
							logWarn("WebForm: dropping MaxLength validator with invalid value", {
								fieldId: field.id,
								value: validator.value
							});
						}
						if (maxLength !== undefined) {
							const effectiveMaxLength = Math.min(maxLength, getMaxLength(field) ?? maxLength);
							const clampedByFieldCeiling = effectiveMaxLength < maxLength;
							// Invariant (LSP-18267): the NUMBER shown is always the effective cap. A stored
							// template is rendered through formatMaxLengthErrorMessage, which substitutes the
							// clamped value, so a 250-worded validator on a 45-capped field still says 45.
							//
							// For non-email fields, wording can still differ: a field whose stored MaxLength is exactly 45 keeps its
							// admin-authored copy, while one clamped down from a looser value gets the generic
							// name copy. That is deliberate — overriding an explicitly configured message would
							// discard copy an admin chose on purpose. Email always uses its canonical character-limit
							// message because stored Email MaxLength configuration is intentionally ignored.
							const errorMessage = resolvesToEmailField(field)
								? `Email address must not exceed ${FIELD_LENGTHS.EMAIL} characters.`
								: field.type === FieldType.Number && clampedByFieldCeiling
									? `Please enter a shorter value (${effectiveMaxLength} characters max).`
									: isNameCappedField(field) && clampedByFieldCeiling
										? `Please enter a shorter name (${effectiveMaxLength} characters max).`
										: formatMaxLengthErrorMessage(validator.errorMessageTpl, field.name, effectiveMaxLength);
							if (field.type === FieldType.Number) {
								numberMaxLengthRule = { maxLength: effectiveMaxLength, errorMessage };
							} else {
								rules.push(
									(resolvesToEmailField(field)
										? VuetifyValidation.MaxCodePointsValidationUntrimmed(effectiveMaxLength, errorMessage)
										: VuetifyValidation.MaxCodePointsValidationUntrimmed(effectiveMaxLength, errorMessage)
									).isValid
								);
							}
							hasValidMaxLength = true;
						}
						break;
					}
					case FieldValidationType.MaxValue:
						// Validator presence signals admin intent — track it regardless of value
						// validity so the safe-ceiling fallback doesn't paper over a misconfig.
						hasMaxValue = true;
						if (validator.value != null && validator.value != undefined && isValidNumber(validator.value)) {
							rules.push(VuetifyValidation.MaxValueValidation(parseInt(validator.value), validator.errorMessageTpl).isValid);
						} else {
							logWarn("WebForm: dropping MaxValue validator with invalid value", {
								fieldId: field.id,
								value: validator.value
							});
						}
						break;
					case FieldValidationType.MinLength:
						if (validator.value != null && validator.value != undefined && isValidNumber(validator.value)) {
							rules.push(VuetifyValidation.MinLengthValidation(parseInt(validator.value), validator.errorMessageTpl).isValid);
						}
						break;
					case FieldValidationType.MinValue:
						hasMinValue = true;
						if (validator.value != null && validator.value != undefined && isValidNumber(validator.value)) {
							rules.push(VuetifyValidation.MinValueValidation(parseInt(validator.value), validator.errorMessageTpl).isValid);
						} else {
							logWarn("WebForm: dropping MinValue validator with invalid value", {
								fieldId: field.id,
								value: validator.value
							});
						}
						break;
					case FieldValidationType.Required:
						if (field.type === FieldType.Number) {
							rules.push(VuetifyValidation.NumberRequiredValidationField(validator.errorMessageTpl).isValid);
						} else {
							rules.push(VuetifyValidation.RequiredValidationField(validator.errorMessageTpl).isValid);
						}
						break;
				}
			}

			// Fallback: enforce max-length on Name fields if no explicit MaxLength validator is configured
			if (isNameCappedField(field)) {
				if (!hasValidMaxLength) {
					rules.push(
						VuetifyValidation.MaxCodePointsValidationUntrimmed(
							FIELD_LENGTHS.NAME,
							`Please enter a shorter name (${FIELD_LENGTHS.NAME} characters max).`
						).isValid
					);
				}
			}
			// resolvesToEmailField only, NOT resolvesToPhoneField, and that asymmetry is deliberate
			// (LSP-18267): an email input uses its 254-character ceiling, whereas a phone field is
			// bounded by PhoneNumberValidation's own
			// FIELD_LENGTHS.PHONE_INPUT limit and renders no character counter in either variant, so a
			// generic 250 length rule would be dead weight rather than a guard.
			if (!hasValidMaxLength && (inheritsDefaultMaxLength(field) || resolvesToEmailField(field))) {
				const fallbackMaxLength = resolvesToEmailField(field) ? FIELD_LENGTHS.EMAIL : FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX;
				const fallbackMessage = resolvesToEmailField(field)
					? `Email address must not exceed ${fallbackMaxLength} characters.`
					: formatMaxLengthErrorMessage(maxLengthErrorTemplate, field.name, fallbackMaxLength);
				rules.push(
					(resolvesToEmailField(field)
						? VuetifyValidation.MaxCodePointsValidationUntrimmed(fallbackMaxLength, fallbackMessage)
						: VuetifyValidation.MaxCodePointsValidationUntrimmed(fallbackMaxLength, fallbackMessage)
					).isValid
				);
			}

			if (field.type === FieldType.Number) {
				// Keep number-format feedback first, then report the enforced character cap before
				// any numeric min/max rule when a value violates both constraints (LSP-19451).
				const numberLengthRule = numberMaxLengthRule ?? {
					maxLength: FIELD_LENGTHS.NUMBER,
					errorMessage: `Please enter a shorter value (${FIELD_LENGTHS.NUMBER} characters max).`
				};
				rules.splice(
					numberLengthRuleInsertionIndex,
					0,
					VuetifyValidation.MaxCodePointsValidationUntrimmed(numberLengthRule.maxLength, numberLengthRule.errorMessage).isValid
				);
				if (!hasMaxValue) {
					rules.push(
						VuetifyValidation.MaxValueValidation(
							FIELD_NUMBER_BOUNDS.MAX,
							`Please enter a number less than or equal to ${FIELD_NUMBER_BOUNDS.MAX}.`
						).isValid
					);
				}
				if (!hasMinValue) {
					rules.push(
						VuetifyValidation.MinValueValidation(
							FIELD_NUMBER_BOUNDS.MIN,
							`Please enter a number greater than or equal to ${FIELD_NUMBER_BOUNDS.MIN}.`
						).isValid
					);
				}
			}

			validationRules.set(field.id, rules);
		}
	}

	/**
	 * True only for a field that is genuinely a name input, and therefore subject to the
	 * FIELD_LENGTHS.NAME ceiling (LSP-18267).
	 *
	 * Keyed on VALIDATOR PRESENCE, never on `field.type` alone, because a Name-typed field that
	 * also carries an Email or Phone validator is treated as an email/phone input by the rest of
	 * the form — WebForm.vue even reassigns `field.type` in place while building its rules. Reading
	 * the mutable type would make the ceiling depend on the order validators happen to appear in
	 * (clamped if MaxLength is processed before Email, not clamped after) and would make the two
	 * WebForm variants disagree, since WebFormV2.vue never mutates the type and instead resolves
	 * the same thing through isEmailField/isPhoneField. An email input must keep its own longer
	 * limit rather than being squeezed into 45.
	 */
	/**
	 * Returns the configured MaxLength for a field so Vuetify can render a live
	 * character counter before the user exceeds the limit (LSP-16444).
	 * Returns undefined for fields without a MaxLength validator (no counter shown).
	 */
	function getMaxLength(field: FormField): number | undefined {
		// Number fields retain a 15-character ceiling (LSP-16444/16831, per Oliver), while a stricter
		// stored MaxLength takes precedence. The API may inject generic MaxLength:250 after save; cap it
		// to 15 so the counter remains stable. setValidationRules enforces the same rule. Mirrors WebFormV2.vue.
		const maxLengthValidator = field.FieldValidators?.find((v: any) => v.type === FieldValidationType.MaxLength);
		const configuredMaxLength = resolvePublicFormMaxLength(field, maxLengthValidator);
		if (configuredMaxLength !== undefined) {
			if (field.type === FieldType.Number) {
				return Math.min(configuredMaxLength, FIELD_LENGTHS.NUMBER);
			}
			// A Name field keeps its own ceiling the same way a Number field does (LSP-18267). The API
			// injects a generic MaxLength:250 into every field on save (contact-api controllers/forms.ts)
			// and getDefaultLayout ships the Name field with 250, which used to WIDEN the Name cap past
			// FIELD_LENGTHS.NAME — so 45 only ever applied to forms that carried no validator at all. A
			// stricter stored MaxLength still wins; a looser one can no longer raise the ceiling.
			if (isNameCappedField(field)) {
				return Math.min(configuredMaxLength, FIELD_LENGTHS.NAME);
			}
			return configuredMaxLength;
		}
		if (field.type === FieldType.Number) {
			return FIELD_LENGTHS.NUMBER;
		}
		// Fallback for Name fields mirrors the setValidationRules fallback
		if (isNameCappedField(field)) {
			return FIELD_LENGTHS.NAME;
		}
		// Email only, not phone — see the note in the rule builder above (LSP-18267).
		if (resolvesToEmailField(field)) {
			return FIELD_LENGTHS.EMAIL;
		}
		if (inheritsDefaultMaxLength(field)) {
			return FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX;
		}
		return undefined;
	}

	function getValidateRules(field: FormField) {
		const rules = validationRules.get(field.id);
		if (field.type === FieldType.Phone) {
			let errorMessage = null;
			for (const rule of rules) {
				const result = rule(phoneNumber.value);
				if (result !== true) {
					errorMessage = result;
					break;
				}
			}
			return errorMessage;
		}
		return rules;
	}

	function isValidNumber(str: string): boolean {
		if (str.trim() === "") {
			return false; // Empty or whitespace-only strings are not valid numbers
		}
		return !isNaN(str);
	}

	function pasteEvent(event: ClipboardEvent) {
		phoneNumber.value = getTrimmedPhoneFromClipboard(event);
	}

	function validatePhoneNumber(args: any) {
		validPhoneNumber.value = args.valid;
	}

	function pasteNumberEvent(event: ClipboardEvent, fieldId: string) {
		if (!fieldId) {
			return;
		}

		const filteredValue = getNumberFromClipboard(event, true);
		formData.value[fieldId] = filteredValue;
	}

	function matchPhone(number: string, phoneObject: any) {
		if (!validPhoneNumber.value) return;
		const phoneField = model.value.FormFields.find((x) => x.type === FieldType.Phone);

		if (phoneField && phoneObject) {
			nextTick(() => {
				phoneNumber.value = phoneObject.formatted;
			});
		}
	}

	function phoneNumberBlur() {
		showPhoneNumberFieldErrors.value = true;
	}

	function phoneNumberFocus() {
		showPhoneNumberFieldErrors.value = false;
	}

	function inputChanged() {}

	function verifyCaptcha() {
		if (captchaState.value === "ready") {
			executeCaptcha();
			return;
		}

		if (captchaState.value === "loading") {
			// LSP-16526: the widget is still loading (the common slow-network case). Hold the
			// submit and let loadCaptcha() replay it the moment render succeeds, rather than
			// calling execute() on a client that does not exist yet.
			captchaSubmitQueued.value = true;
			logInfo("[webform-captcha] submit queued while the widget was still loading", {
				attempts: captchaLoadCount.value
			});
			return;
		}

		// "unavailable" — the inline notice already explains it and offers a retry.
	}

	/**
	 * The single validity gate: validates the form AND reveals the phone field's errors (it is a
	 * submit-attempt action, not a pure predicate). Every path that can reach submitForm() runs
	 * through this, because
	 * the fields stay editable while the captcha loads AND while Google's challenge is open — so
	 * what was valid when the user tapped Submit may not be valid by the time we post (break-it
	 * round 2: a retry, or a mid-challenge edit, could post a lead with a blank required Name).
	 */
	async function validateForSubmit(): Promise<boolean> {
		// The <v-form> is gone once the success panel replaces it; nothing left to submit.
		if (!form.value) {
			return false;
		}

		const { valid } = await form.value.validate();
		showPhoneNumberFieldErrors.value = true;

		// break-it round 3: only require the phone widget's own validity when the form actually HAS
		// a phone field. validPhoneNumber is fed by vue-tel-input's @validate, which never fires
		// when no phone field is rendered — so a perfectly valid phone-less V1 form was refused in
		// silence forever, the same "Submit does nothing" dead end this ticket is about.
		const phoneNumberAcceptable = !hasPhoneNumberField.value || validPhoneNumber.value;

		if (valid && !phoneNumberAcceptable) {
			// The other refusal (a failing field rule) shows itself in red on the field; this one
			// used to be invisible, so it is the one worth a diagnostic.
			logWarn("[webform-captcha] submit refused because the phone number is not valid yet", {
				formId: formIdValue.value
			});
		}

		return !!valid && phoneNumberAcceptable;
	}

	async function validateForm() {
		if (!(await validateForSubmit())) {
			return;
		}

		if (!captchaEnabled.value) {
			await submitForm();
		} else {
			verifyCaptcha();
		}
	}

	async function submitForm() {
		// break-it round 1 (pre-existing, and reachable via the new replay path): Google can invoke
		// the render callback more than once, and each invocation lands here — concurrently (saving
		// still true) or after the first one finished (shareUrl already set, success panel showing).
		// Either way the second one is a duplicate lead. saving is reset in this function's own
		// finally, so refusing a re-entrant call cannot wedge the button.
		if (saving.value || shareUrl.value) {
			logWarn("[webform-captcha] ignoring a duplicate submit", {
				formId: formIdValue.value,
				reason: saving.value ? "one is already in flight" : "this form was already submitted"
			});
			return;
		}

		saving.value = true;
		const fieldSubmissions = new Array<FormSubmissionField>();

		try {
			for (let id of Object.keys(formData.value)) {
				const field = model.value.FormFields.find((x) => x.id == id);

				if (!field) {
					continue;
				}

				let type = FieldType.Text;

				if (field.type === FieldType.Date) {
					type = FieldType.Date;
				} else if (field.type === FieldType.DateTime) {
					type = FieldType.DateTime;
				} else if (field.type === FieldType.Number) {
					type = FieldType.Number;
				} else if (field.type === FieldType.Phone) {
					formData.value[id] = phoneNumber.value;
				}

				const fieldSubmission = {
					fieldId: id,
					type: type,
					value: formData.value[id]
				} as FormSubmissionField;
				fieldSubmissions.push(fieldSubmission);
			}

			const nameFormField = model.value.FormFields.find((x) => constants.areEqual(x.name, "Name"));
			const currentDate = timezones.formatDate(new Date().toISOString(), getDateTokens(orgDateFormat.value).dateShortMonth);
			let conversationName = ""; // Recording from [FIRST NAME OF CONTACT] on [M/D/YYYY]
			if (fieldSubmissions.length > 0) {
				const nameFieldSubmission = fieldSubmissions.find((x) => x.fieldId == nameFormField?.id);
				if (nameFieldSubmission) {
					conversationName = `Recording from ${nameFieldSubmission.value.split(" ")[0]} on ${currentDate}`;
				}
			}

			// Add honeypot field (should be empty)
			fieldSubmissions.push({
				fieldId: PUBLIC_FORM_HONEYPOT_FIELD_NAME,
				type: FieldType.Text,
				value: honeypotValue.value
			} as FormSubmissionField);

			// Add form ID field (set with JavaScript)
			fieldSubmissions.push({
				fieldId: "__fid",
				type: FieldType.Text,
				value: formIdValue.value
			} as FormSubmissionField);

			const formSubmission = {
				formId: model.value.id,
				organizationId: model.value.organizationId,
				FormSubmissionFields: fieldSubmissions,
				forcedUserId: model.value.createdBy
			} as FormSubmission;

			if (conversationName) {
				formSubmission.conversationName = conversationName;
			}

			const response = await api.submitCustomWebForm(formSubmission);
			shareUrl.value = response.customerUrl;

			const success = await copyToClipboard(shareUrl.value);
			if (success) {
				const message = "Form link copied to clipboard.";
				toast.success(message, { timeout: calculateTimeout(message) });
			}
		} catch (err) {
			if (err?.httpStatus === 404) {
				logError("WebForm: public form submission returned 404", getDiagnosticError(err), {
					formId: model.value.id,
					httpStatus: err?.httpStatus,
					code: err?.code
				});
				formDeleted.value = true;
				const message = "Sorry, this form has been deleted and can no longer be used to record a video.";
				toast.error(message, {
					timeout: false,
					closeOnClick: false,
					pauseOnHover: false,
					pauseOnFocusLoss: false
				});
			} else {
				const isTrustedApiValidation =
					err?.httpStatus >= 400 && err?.httpStatus < 500 && err?.isJson === true && err?.userFacing === true;
				const message = isTrustedApiValidation && typeof err?.message === "string" && err.message.trim() ? err.message : undefined;
				if (!message) {
					const diagnostics = getPublicSubmissionFailureDiagnostics(err);
					logError("WebForm: using generic public submission error", diagnostics.error, {
						formId: model.value.id,
						...diagnostics.context
					});
				}
				toast.error(message || "An error occurred while submitting the form. Please try again.", {
					timeout: false,
					closeOnClick: false,
					pauseOnHover: false,
					pauseOnFocusLoss: false
				});
			}

			console.error(err, {
				tags: {
					method: "submitForm",
					file: "WebForm"
				},
				extra: {
					formId: model.value.id,
					formData: formData.value
				}
			});
		} finally {
			saving.value = false;
		}
	}

	const posterClass = computed(() => {
		const posterClass = organizationLogo.value ? "org-poster" : "poster";
		const sizeSpecificClass = display.smAndDown.value ? "" : "my-12";
		return `${posterClass} ${sizeSpecificClass} mx-auto`;
	});

	function isRequired(field: FormField): boolean {
		const validator = field.FieldValidators?.find((x) => x.type == FieldValidationType.Required);
		return validator != null;
	}

	// Delegates to the shared predicate (LSP-18267) — same drift as isEmailField above.
	function isPhoneNumberField(field: FormField): boolean {
		return resolvesToPhoneField(field);
	}

	const hasPhoneNumberField = computed(() => (model.value.FormFields ?? []).some((field) => isPhoneNumberField(field as FormField)));

	// Delegates to the shared predicate (LSP-18267). This used to accept only the validator arm
	// while WebFormV2 accepted the declared type too, so an Email-TYPED field with no Email
	// validator matched no template branch here and rendered no input at all.
	function isEmailField(field: FormField): boolean {
		return resolvesToEmailField(field);
	}

	function getLabel(field: FormField, requiredOverride?: boolean) {
		const required = requiredOverride || isRequired(field);
		const label = `${field.name}${required ? " (Required)" : " (Optional)"}`;
		return label;
	}
</script>

<style lang="scss" scoped>
	$xs: 402px;
	$small: 600px;
	$medium: 900px;
	$large: 901px;

	#app {
		display: block !important;
	}
	nav .v-icon {
		padding-right: 8px;
		font-size: 18px;
	}
	.action {
		cursor: pointer;
	}
	.main {
		background-image: url("@/assets/ls-contact-loader-wallpaper-v2.svg");
		background-size: cover;
		background-position: center;
		background-color: #f5f5f5;
		min-height: 100vh;
	}
	.main .header {
		background-color: #dff0ff;
	}

	.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
		background-color: #0070ff !important;
		opacity: 0.24;
	}
	.theme--light.v-btn.v-btn--disabled {
		color: white !important;
	}
	.theme--light.v-btn.v-btn--disabled .v-icon,
	.theme--light.v-btn.v-btn--disabled .v-btn__loading {
		color: white !important;
	}

	.text-h5 {
		@media screen and (max-width: $small) {
			font-size: 1rem !important;
		}
	}

	.recording-video-img {
		display: flex;
		max-width: 100%;
		max-height: 200px;
		margin-top: auto;
		margin-left: auto;
		margin-right: auto;
		@media screen and (max-width: $small) {
			max-height: 150px;
		}
	}
	.v-snack__content {
		font-size: 1rem;
	}

	.mobile-container-row {
		padding-top: 20px !important;
		padding-bottom: 70px;
	}
	.poster {
		height: 50px;
		display: flex;
		max-width: 90%;
		object-fit: contain;
		@media screen and (max-width: $medium) {
			display: initial;
		}
	}
	.org-poster {
		height: 125px;
		display: flex;
		max-width: 90%;
		object-fit: contain;
		@media screen and (max-width: $medium) {
			display: initial;
			height: 50px;
		}
	}
	.on-top {
		z-index: 5 !important;
	}

	.recording-information {
		padding: 15px;
	}

	#create-new-link {
		font-size: 14px;
		border: 1px solid #fff;
		margin-top: 10px;
	}

	.copy-recording-link {
		margin-right: 8px;
	}

	.button .v-btn {
		border-radius: 12px;
	}

	:deep(.v-text-field .v-messages__message) {
		line-height: 1rem !important;
	}
</style>
